import { useState, type ReactNode } from 'react';
import { Award, CalendarX } from 'lucide-react';
import { isSingleEventActivity } from 'shared';
import { useAwardBadge, useCancelSession, type ActivitySessionDetail } from '@/hooks/useActivities';
import { ConfirmDialog, TypeChip, UniversoChip } from './ActivityUi';
import SessionDetailsForm from './SessionDetailsForm';
import SessionQrCard from './SessionQrCard';
import SessionRosterCard from './SessionRosterCard';
import { formatSessionDate, formatSessionWhen } from '@/lib/activityTime';
import { awardSummary, participants } from '@/lib/sessionDetails';

type DialogState = 'none' | 'award' | 'cancel';

interface SessionBoardProps {
  session: ActivitySessionDetail;
  /** Buttons that go before the board's own, such as editing the activity. */
  actions?: ReactNode;
  /** What goes between the heading and the code, such as what the activity is. */
  children?: ReactNode;
  /** Called once the session has been cancelled, to leave the page. */
  onCancelled: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

/**
 * A session in front of whoever is running it: the code to show, the roster
 * filling in, the visiting group and the session's details. An open day is
 * one session and nothing else, so for it this is the whole event and says so.
 */
export default function SessionBoard({
  session,
  actions,
  children,
  onCancelled,
  onSuccess,
  onError,
}: SessionBoardProps) {
  const award = useAwardBadge();
  const cancel = useCancelSession();
  const [dialog, setDialog] = useState<DialogState>('none');

  const openDay = isSingleEventActivity(session.activityType);
  const cancelLabel = openDay ? 'Cancelar Dia Aberto' : 'Cancelar sessão';

  const handleAward = async () => {
    try {
      const result = await award.mutateAsync(session.id);
      onSuccess(awardSummary(result));
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'Erro ao atribuir a credencial.');
    }
    setDialog('none');
  };

  const handleCancel = async () => {
    try {
      await cancel.mutateAsync(session.id);
      onCancelled();
    } catch (err: unknown) {
      onError(
        err instanceof Error
          ? err.message
          : openDay
            ? 'Erro ao cancelar o Dia Aberto.'
            : 'Erro ao cancelar a sessão.',
      );
      setDialog('none');
    }
  };

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground">{session.activityName}</h1>
            <TypeChip type={session.activityType} />
            {session.universo && <UniversoChip universo={session.universo} />}
          </div>
          <p className="text-muted-foreground mt-1">
            {formatSessionWhen(session.startsAt, session.endsAt)} · {session.digitalCenterName} ·{' '}
            {session.leaderName}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          {session.badgeId && (
            <button
              type="button"
              onClick={() => setDialog('award')}
              disabled={session.attendances.length === 0}
              title={
                session.attendances.length === 0
                  ? 'Ainda não há participantes presentes'
                  : undefined
              }
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Award className="w-4 h-4" />
              Atribuir credencial
            </button>
          )}
          <button
            type="button"
            onClick={() => setDialog('cancel')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-destructive/30 bg-destructive/5 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
          >
            <CalendarX className="w-4 h-4" />
            {cancelLabel}
          </button>
        </div>
      </div>

      {children}

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        <SessionQrCard session={session} />
        <SessionRosterCard session={session} onSuccess={onSuccess} onError={onError} />
      </div>

      <SessionDetailsForm
        key={session.id}
        session={session}
        onSuccess={onSuccess}
        onError={onError}
      />

      {dialog === 'award' && (
        <ConfirmDialog
          title="Atribuir credencial"
          confirmLabel="Atribuir"
          tone="primary"
          isPending={award.isPending}
          onConfirm={handleAward}
          onClose={() => setDialog('none')}
        >
          A credencial{' '}
          <span className="font-semibold text-foreground">{session.badgeName}</span> vai ser
          atribuída a {participants(session.attendances.length)} presente
          {session.attendances.length !== 1 ? 's' : ''} {openDay ? 'neste Dia Aberto' : 'nesta sessão'}.
          Quem já a tem fica como está.
        </ConfirmDialog>
      )}
      {dialog === 'cancel' && (
        <ConfirmDialog
          title={cancelLabel}
          confirmLabel={cancelLabel}
          isPending={cancel.isPending}
          onConfirm={handleCancel}
          onClose={() => setDialog('none')}
        >
          {openDay ? (
            <>
              Tens a certeza que queres cancelar{' '}
              <span className="font-semibold text-foreground">"{session.activityName}"</span>? O
              código deixa de funcionar e o Dia Aberto sai da lista de atividades.
            </>
          ) : (
            <>
              Tens a certeza que queres cancelar a sessão de{' '}
              <span className="font-semibold text-foreground">
                {formatSessionDate(session.startsAt)}
              </span>
              ? O código deixa de funcionar e a sessão sai da lista.
            </>
          )}
          {session.attendances.length > 0 &&
            ` As ${session.attendances.length} presenças já registadas ficam guardadas.`}
        </ConfirmDialog>
      )}
    </>
  );
}
